<template>
  <div class="mt-4 flex flex-col items-center gap-12 md:flex-row">
    <Box
      :style="{
        '--bg-color': colors[campaignFromNumber(campaignId)],
      }"
      rounded="unset"
      border="lg"
      shadow="unset"
      class="flex size-[160px] flex-col items-center rounded-3xl bg-[--bg-color] px-3 py-4 shadow-[6px_6px_0px_0px] transition-colors"
    >
      <NuxtImg aria-hidden="true" :src="icon" class="size-full" />
    </Box>

    <div class="flex flex-col justify-center space-y-3">
      <NfcCurvyText
        :text="name"
        text-size="text-4xl whitespace-nowrap leading-none"
        text-outline-class="text-outline-mobile"
        top-positioning="top-[0.080em]"
        left-positioning="left-[0.050em]"
        :text-color="typeColors.text"
        :text-shadow-color="typeColors.textShadow"
      />
      <div class="space-y-2">
        <NfcProgressBar
          ref="progressBar"
          class="max-w-[288px]"
          :previous-progress="previousProgressData.progress"
          :progress="props.progress"
          :previous-level="previousProgressData.level"
          :level="props.level"
          :progress-bg-color="typeColors.textShadow"
          :is-not-animated="noNeedToAnimate"
          @animationend="stopAnimation"
        />
        <p
          class="whitespace-nowrap font-Kontesa text-[20px] text-navy outline-none"
        >
          <span class="font-bold">{{ name }} #{{ number }}</span>
          : {{ owner }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import NfcProgressBar from "~/components/Nfc/ProgressBar.vue";
import {
  useNfcTypeColors,
  useNfcName,
  useNfcShirtIcon,
} from "~/domains/nfc/composables";
import type { NFCType } from "~/domains/nfc/types";
import { campaignFromNumber, colors } from "~/lib/campaigns";
import { usePreference } from "~/domains/member/composables";
import { useBodyScrollLock } from "radix-vue";

const progressBar = ref<InstanceType<typeof NfcProgressBar> | null>(null);
const noNeedToAnimate = ref(true);

const props = defineProps<{
  type: NFCType;
  campaignId: number;
  owner: string;
  number: number;
  progress: number;
  level: number;
  nfcId: string;
}>();

const scrollLock = useBodyScrollLock();

const previousProgressData = usePreference(
  computed(() => ({
    key: `nfc-shirt-${props.nfcId}-last-progress-and-level`,
    defaultValue: {
      progress: 0,
      level: 0,
      impact: 0,
    },
  })),
);

const name = useNfcName(() => props.type);
const typeColors = useNfcTypeColors(() => props.type);

const icon = useNfcShirtIcon(() => props.type);

const stopAnimation = () => {
  previousProgressData.value = {
    ...previousProgressData.value,
    progress: props.progress,
    level: props.level,
  };
  scrollLock.value = false;
};

onMounted(() => {
  scrollLock.value = true;
  if (progressBar.value && !noNeedToAnimate.value)
    progressBar.value.playAnimation();
});

watchEffect(() => {
  noNeedToAnimate.value =
    props.progress === previousProgressData.value.progress &&
    props.level === previousProgressData.value.level;
});
</script>
